import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import AppShell from "./AppShell";
import { 
  FileText, Users, Settings, Mail, Link2, Download,
  CreditCard, BarChart3, Smartphone, ChevronDown, ChevronRight,
  Zap, CheckCircle
} from "lucide-react";

const guides = [
  {
    id: "settings",
    icon: Settings,
    title: "Set up your business profile",
    steps: [
      { text: "Go to Settings to add your business name and address", link: "/settings" },
      { text: "Upload your logo — it'll appear on all invoices and PDFs" },
      { text: "Select your country for automatic tax configuration" },
      { text: "Add bank details or PayPal for payment instructions on invoices" },
      { text: "Connect your Stripe, Razorpay, or Wise accounts so clients can pay online" },
      { text: "Set a custom reply-to email for invoice emails" },
    ]
  },
  {
    id: "first-invoice",
    icon: FileText,
    title: "Create your first invoice",
    steps: [
      { text: "Click \"New invoice\" from the dashboard", link: "/invoices/new" },
      { text: "Select a client (or create one inline)" },
      { text: "Add line items — description, quantity, and rate" },
      { text: "Tax lines auto-fill based on your country setting" },
      { text: "Check the total, set a due date" },
      { text: "Click \"Save & send\" to email it, or \"Save draft\" to send later" },
    ]
  },
  {
    id: "clients",
    icon: Users,
    title: "Manage your clients",
    steps: [
      { text: "Go to Clients from the sidebar", link: "/clients" },
      { text: "Click \"Add client\" to save their details" },
      { text: "Name, email, company, address, and phone are all stored" },
      { text: "Next time you create an invoice, just pick them from the dropdown" },
    ]
  },
  {
    id: "payments",
    icon: CreditCard,
    title: "Get paid online",
    steps: [
      { text: "Connect your Stripe account in Settings to accept card payments", link: "/settings" },
      { text: "Add your PayPal.me username for PayPal payments" },
      { text: "Connect Razorpay for UPI payments (popular in India)" },
      { text: "Add your Wise email for international bank transfers" },
      { text: "Payment buttons appear automatically on shared invoices" },
    ]
  },
  {
    id: "email",
    icon: Mail,
    title: "How invoice emails work",
    steps: [
      { text: "Emails are sent from VeroBill on your behalf — no setup needed" },
      { text: "Your business name appears in the email, with your custom message" },
      { text: "Client replies go to your reply-to email (set in Settings)", link: "/settings" },
      { text: "Each email includes a link to view and pay the invoice online" },
    ]
  },
  {
    id: "share",
    icon: Link2,
    title: "Share invoices",
    steps: [
      { text: "Every invoice gets a unique shareable link" },
      { text: "Click the share icon on any invoice to copy the link" },
      { text: "Clients can view, download the PDF, and pay — no login needed" },
      { text: "You can also download the PDF yourself for offline use" },
    ]
  },
  {
    id: "analytics",
    icon: BarChart3,
    title: "Track your revenue",
    steps: [
      { text: "Go to Analytics from the sidebar", link: "/analytics" },
      { text: "View revenue by month — paid vs outstanding" },
      { text: "Filter by client or date range" },
      { text: "See your top clients, collection rate, and average invoice value" },
    ]
  },
  {
    id: "pwa",
    icon: Smartphone,
    title: "Install on your phone",
    steps: [
      { text: "Open VeroBill in your mobile browser (Safari or Chrome)" },
      { text: "iOS: Tap Share → \"Add to Home Screen\"" },
      { text: "Android: Tap the menu → \"Install app\" or \"Add to Home Screen\"" },
      { text: "It works like a native app — full screen, fast, and offline-ready" },
    ]
  }
];

const GettingStarted = () => {
  const [openGuide, setOpenGuide] = useState("settings");

  return (
    <AppShell active="help">
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Zap className="w-5 h-5 text-[#1A3DFF]" />
          <h1 className="font-display text-[22px] sm:text-[28px] tracking-[-0.03em] text-[#0A0A0A]">Getting Started</h1>
        </div>
        <p className="text-[14px] text-[#6B7280]">Quick guides to help you get the most out of VeroBill.</p>
      </div>

      <div className="max-w-2xl space-y-3">
        {guides.map((guide) => {
          const isOpen = openGuide === guide.id;
          const Icon = guide.icon;
          return (
            <div key={guide.id} className="bg-white border border-[#E5E7EB] overflow-hidden" data-testid={`guide-${guide.id}`}>
              <button
                onClick={() => setOpenGuide(isOpen ? null : guide.id)}
                className="w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-[#F9FAFB] transition-colors"
                data-testid={`guide-toggle-${guide.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#0A0A0A]" />
                  </div>
                  <span className="font-semibold text-[15px] text-[#0A0A0A]">{guide.title}</span>
                </div>
                {isOpen ? <ChevronDown className="w-4 h-4 text-[#9CA3AF]" /> : <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />}
              </button>
              {isOpen && (
                <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-0">
                  <div className="ml-12 space-y-3">
                    {guide.steps.map((step, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="w-5 h-5 bg-[#1A3DFF]/5 border border-[#1A3DFF]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-[10px] font-bold text-[#1A3DFF]">{i + 1}</span>
                        </div>
                        <p className="text-[14px] text-[#374151] leading-relaxed">
                          {step.text}
                          {step.link && (
                            <Link to={step.link} className="text-[#1A3DFF] text-[13px] font-medium hover:underline ml-1.5">Go &rarr;</Link>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </AppShell>
  );
};

export default GettingStarted;
